<template>
  <div class="px-wrap">
    <div class="px-header">
      <div>
        <h2 class="px-title">
          <feather-icon icon="CrosshairIcon" size="20" class="mr-75" />
          Pixels &amp; Audiencias
        </h2>
        <p class="px-sub">Configure pixels para criar publicos personalizados com base no comportamento do visitante no video</p>
      </div>
      <div class="px-header-actions">
        <button class="px-btn-back" @click="goBackToVideo">
          <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
          Voltar ao vídeo
        </button>
        <button class="px-btn-add" @click="openModal()">
          <feather-icon icon="PlusIcon" size="14" class="mr-50" />
          Novo Pixel
        </button>
      </div>
    </div>

    <!-- Info -->
    <div class="px-info">
      <feather-icon icon="InfoIcon" size="14" class="mr-75" style="flex-shrink:0" />
      <span>Adicione seus pixels do <strong>Facebook</strong>, <strong>Google Ads</strong> ou <strong>TikTok</strong> e defina em qual segundo do video cada evento deve disparar. Isso permite criar <strong>publicos personalizados</strong> para remarketing.</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="px-loading">Carregando pixels...</div>

    <!-- Lista vazia -->
    <div v-else-if="!pixels.length" class="px-empty">
      <feather-icon icon="CrosshairIcon" size="40" class="mb-1" style="opacity:0.3" />
      <p>Nenhum pixel configurado para este video</p>
      <button class="px-btn-add" @click="openModal()">Adicionar primeiro pixel</button>
    </div>

    <!-- Lista de pixels -->
    <div v-else class="px-list">
      <div v-for="px in pixels" :key="px.id" class="px-card">
        <div class="px-card-header">
          <div class="px-card-platform">
            <span class="px-platform-badge" :class="'px-platform-' + px.platform">{{ platformLabel(px.platform) }}</span>
            <code class="px-pixel-id">{{ px.pixel_id }}</code>
          </div>
          <div class="px-card-actions">
            <label class="px-toggle">
              <input type="checkbox" :checked="px.ativo" @change="toggleAtivo(px)">
              <span class="px-toggle-slider"></span>
            </label>
            <button class="px-btn-icon" title="Editar" @click="openModal(px)">
              <feather-icon icon="EditIcon" size="14" />
            </button>
            <button class="px-btn-icon px-btn-danger" title="Remover" @click="removePixel(px)">
              <feather-icon icon="TrashIcon" size="14" />
            </button>
          </div>
        </div>
        <div class="px-card-events">
          <div v-for="(evt, i) in parseEvents(px.events)" :key="i" class="px-event-tag">
            <feather-icon icon="ZapIcon" size="11" class="mr-25" />
            <strong>{{ evt.name }}</strong>
            <span class="px-event-at">aos {{ formatTrigger(evt.trigger_at) }}</span>
          </div>
          <div v-if="!parseEvents(px.events).length" class="px-event-empty">Nenhum evento configurado</div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="modal.show" class="px-modal-overlay" @click.self="modal.show = false">
      <div class="px-modal">
        <div class="px-modal-header">
          <h3>{{ modal.editing ? 'Editar Pixel' : 'Novo Pixel' }}</h3>
          <button class="px-btn-icon" @click="modal.show = false"><feather-icon icon="XIcon" size="18" /></button>
        </div>
        <div class="px-modal-body">
          <div class="px-field">
            <label>Plataforma</label>
            <select v-model="modal.form.platform" class="px-input">
              <option value="facebook">Facebook / Meta</option>
              <option value="google">Google Ads</option>
              <option value="tiktok">TikTok</option>
              <option value="taboola">Taboola</option>
              <option value="custom">Outro (DataLayer)</option>
            </select>
          </div>
          <div class="px-field">
            <label>ID do Pixel</label>
            <input v-model="modal.form.pixel_id" class="px-input" :placeholder="pixelPlaceholder" />
          </div>

          <div class="px-field">
            <label>Eventos</label>
            <p class="px-hint">Defina quais eventos disparar e em qual momento do video</p>
            <div v-for="(evt, i) in modal.form.events" :key="i" class="px-event-row">
              <input v-model="evt.name" class="px-input px-input-sm" placeholder="Nome do evento (ex: ViewContent)" />
              <input v-model="evt.trigger_at" class="px-input px-input-sm px-input-trigger" placeholder="Segundo ou %" />
              <button class="px-btn-icon px-btn-danger" @click="modal.form.events.splice(i, 1)">
                <feather-icon icon="MinusCircleIcon" size="14" />
              </button>
            </div>
            <button class="px-btn-add-event" @click="modal.form.events.push({ name: '', trigger_at: '' })">
              <feather-icon icon="PlusIcon" size="13" class="mr-25" />
              Adicionar evento
            </button>
          </div>

          <!-- Sugestoes rapidas -->
          <div class="px-suggestions">
            <span class="px-hint">Sugestoes rapidas:</span>
            <button class="px-suggestion" @click="addSuggestion('PageView', 0)">PageView (inicio)</button>
            <button class="px-suggestion" @click="addSuggestion('ViewContent', '25%')">ViewContent (25%)</button>
            <button class="px-suggestion" @click="addSuggestion('Lead', '50%')">Lead (50%)</button>
            <button class="px-suggestion" @click="addSuggestion('AddToCart', '75%')">AddToCart (75%)</button>
            <button class="px-suggestion" @click="addSuggestion('Purchase', '95%')">Purchase (95%)</button>
          </div>
        </div>
        <div class="px-modal-footer">
          <button class="px-btn-cancel" @click="modal.show = false">Cancelar</button>
          <button class="px-btn-save" @click="savePixel" :disabled="saving">
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '@/composables/useApi'

const { api: apiBack } = useApi()

export default {
  name: 'PixelConfig',
  props: {
    vslId: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      saving: false,
      pixels: [],
      modal: {
        show: false,
        editing: null,
        form: { platform: 'facebook', pixel_id: '', events: [{ name: 'PageView', trigger_at: 0 }] },
      },
    }
  },
  computed: {
    pixelPlaceholder() {
      const m = { facebook: 'Ex: 123456789012345', google: 'Ex: AW-123456789', tiktok: 'Ex: C123ABC456' }
      return m[this.modal.form.platform] || 'ID do pixel'
    },
  },
  mounted() { this.fetchPixels() },
  methods: {
    goBackToVideo() {
      const vslId = this.vslId || this.$route?.params?.vslId
      if (vslId) {
        this.$router.push(`/player/${vslId}`)
        return
      }
      this.$router.back()
    },
    async fetchPixels() {
      this.loading = true
      try {
        const { data } = await apiBack.get(`/pixels/${this.vslId}`)
        this.pixels = data
      } catch { this.pixels = [] }
      this.loading = false
    },
    parseEvents(events) {
      if (!events) return []
      return typeof events === 'string' ? JSON.parse(events) : events
    },
    platformLabel(p) {
      return { facebook: 'Meta / Facebook', google: 'Google Ads', tiktok: 'TikTok', taboola: 'Taboola', custom: 'Custom' }[p] || p
    },
    formatTrigger(val) {
      const s = String(val)
      if (s.endsWith('%')) return s
      const n = Number(s)
      if (n === 0) return 'inicio'
      if (n < 60) return `${n}s`
      return `${Math.floor(n / 60)}m${n % 60 ? (n % 60) + 's' : ''}`
    },
    openModal(px) {
      if (px) {
        this.modal.editing = px.id
        this.modal.form = {
          platform: px.platform,
          pixel_id: px.pixel_id,
          events: JSON.parse(JSON.stringify(this.parseEvents(px.events))),
        }
      } else {
        this.modal.editing = null
        this.modal.form = { platform: 'facebook', pixel_id: '', events: [{ name: 'PageView', trigger_at: 0 }] }
      }
      this.modal.show = true
    },
    addSuggestion(name, triggerAt) {
      this.modal.form.events.push({ name, trigger_at: triggerAt })
    },
    async savePixel() {
      if (!this.modal.form.pixel_id) return this.$message({ type: 'error', message: 'Informe o ID do pixel' })
      this.saving = true
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        const payload = { ...this.modal.form, id_vsl: this.vslId, id_user: userData.id }
        if (this.modal.editing) {
          await apiBack.put(`/pixels/${this.modal.editing}`, payload)
        } else {
          await apiBack.post('/pixels', payload)
        }
        this.modal.show = false
        this.$message({ type: 'success', message: 'Pixel salvo!' })
        this.fetchPixels()
      } catch (e) {
        this.$message({ type: 'error', message: 'Erro ao salvar pixel' })
      }
      this.saving = false
    },
    async toggleAtivo(px) {
      await apiBack.put(`/pixels/${px.id}`, { ...px, events: this.parseEvents(px.events), ativo: px.ativo ? 0 : 1 })
      this.fetchPixels()
    },
    async removePixel(px) {
      if (!confirm('Remover este pixel?')) return
      await apiBack.delete(`/pixels/${px.id}`)
      this.fetchPixels()
    },
  },
}
</script>

<style lang="scss" scoped>
$orange: #E86A10;
$border: #e2e8f0;
$bg: #f8fafc;

.px-wrap { padding: 0; }
.px-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
.px-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.px-title {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 800;
  color: #f8fafc !important;
  margin: 0 0 4px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);

  svg,
  .feather-icon {
    color: inherit !important;
    fill: currentColor !important;
  }
}
.px-sub { font-size: 13px; color: rgba(248,250,252,0.74); margin: 0; max-width: 500px; text-shadow: 0 1px 2px rgba(0, 0, 0, 0.28); }

.px-btn-back {
  display: inline-flex; align-items: center; padding: 10px 16px; border-radius: 10px;
  background: #0f172a; color: #fff; border: none; font-weight: 700; font-size: 13px;
  cursor: pointer; transition: opacity .18s;
  &:hover { opacity: 0.9; }
}
.px-btn-add {
  display: inline-flex; align-items: center; padding: 10px 20px; border-radius: 10px;
  background: $orange; color: #fff; border: none; font-weight: 700; font-size: 13px;
  cursor: pointer; transition: opacity .18s;
  &:hover { opacity: 0.9; }
}

.px-info {
  display: flex; align-items: flex-start; padding: 14px 18px; border-radius: 10px;
  background: #FFF7ED; border: 1px solid #FDBA74; color: #9A3412; font-size: 13px;
  line-height: 1.6; margin-bottom: 20px;
}

.px-loading, .px-empty { text-align: center; padding: 60px 20px; color: #94a3b8; font-size: 14px; }

.px-list { display: flex; flex-direction: column; gap: 12px; }
.px-card {
  background: #fff; border: 1.5px solid $border; border-radius: 12px; overflow: hidden;
  transition: border-color .18s;
  &:hover { border-color: $orange; }
}
.px-card-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid $border; }
.px-card-platform { display: flex; align-items: center; gap: 10px; }
.px-platform-badge {
  padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
}
.px-platform-facebook { background: #E8F0FE; color: #1877F2; }
.px-platform-google { background: #FEF7E0; color: #EA4335; }
.px-platform-tiktok { background: #F0F0F0; color: #000; }
.px-platform-taboola { background: #E0F0FF; color: #004D99; }
.px-platform-custom { background: #F0F0F0; color: #475569; }
.px-pixel-id { font-size: 12px; color: #64748b; background: $bg; padding: 2px 8px; border-radius: 4px; }

.px-card-actions { display: flex; align-items: center; gap: 8px; }
.px-btn-icon { border: none; background: none; cursor: pointer; color: #64748b; padding: 4px; border-radius: 6px; transition: color .18s; &:hover { color: $orange; } }
.px-btn-danger:hover { color: #EF4444 !important; }

.px-card-events { padding: 12px 18px; display: flex; flex-wrap: wrap; gap: 8px; }
.px-event-tag {
  display: inline-flex; align-items: center; padding: 5px 12px; border-radius: 6px;
  background: $bg; border: 1px solid $border; font-size: 12px; color: #334155;
}
.px-event-at { margin-left: 6px; color: $orange; font-weight: 600; font-size: 11px; }
.px-event-empty { font-size: 12px; color: #94a3b8; }

/* Toggle */
.px-toggle { position: relative; width: 36px; height: 20px; display: inline-block; }
.px-toggle input { opacity: 0; width: 0; height: 0; }
.px-toggle-slider {
  position: absolute; inset: 0; background: #cbd5e1; border-radius: 20px; cursor: pointer; transition: .2s;
  &::before { content: ''; position: absolute; width: 16px; height: 16px; border-radius: 50%; background: #fff; top: 2px; left: 2px; transition: .2s; }
}
.px-toggle input:checked + .px-toggle-slider { background: $orange; }
.px-toggle input:checked + .px-toggle-slider::before { left: 18px; }

/* Modal */
.px-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 10000; display: flex; align-items: center; justify-content: center; }
.px-modal { background: #fff; border-radius: 14px; width: 520px; max-width: 95vw; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
.px-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid $border; }
.px-modal-header h3 { margin: 0; font-size: 16px; font-weight: 700; color: #0f172a; }
.px-modal-body { padding: 24px; }
.px-modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid $border; }

.px-field { margin-bottom: 18px; }
.px-field label { display: block; font-size: 12px; font-weight: 700; color: #0f172a; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
.px-hint { font-size: 12px; color: #94a3b8; margin-bottom: 8px; display: block; }
.px-input {
  width: 100%; padding: 10px 14px; border: 1.5px solid $border; border-radius: 8px;
  font-size: 13px; color: #0f172a; background: #fff; outline: none; transition: border-color .18s;
  &:focus { border-color: $orange; }
}
.px-input-sm { padding: 8px 12px; }
.px-input-trigger { width: 100px; flex-shrink: 0; }

.px-event-row { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
.px-btn-add-event {
  display: inline-flex; align-items: center; padding: 6px 14px; border-radius: 6px;
  border: 1.5px dashed $border; background: none; color: #64748b; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all .18s;
  &:hover { border-color: $orange; color: $orange; }
}

.px-suggestions { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin-top: 4px; }
.px-suggestion {
  padding: 4px 10px; border-radius: 6px; border: 1px solid $border; background: $bg;
  font-size: 11px; color: #475569; cursor: pointer; transition: all .18s;
  &:hover { border-color: $orange; color: $orange; background: #FFF7ED; }
}

.px-btn-cancel { padding: 10px 20px; border-radius: 8px; border: 1.5px solid $border; background: #fff; color: #475569; font-weight: 600; font-size: 13px; cursor: pointer; }
.px-btn-save {
  padding: 10px 24px; border-radius: 8px; border: none; background: $orange; color: #fff;
  font-weight: 700; font-size: 13px; cursor: pointer; transition: opacity .18s;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.dark-layout {
  .px-title { color: #f8fafc !important; }
  .px-sub { color: rgba(248,250,252,0.72) !important; }
  .px-btn-back {
    background: rgba(255,255,255,0.08) !important;
    color: #f8fafc !important;
    border: 1px solid rgba(255,255,255,0.12) !important;
    &:hover { background: rgba(255,255,255,0.14) !important; }
  }
}
</style>
